import z from 'zod';
import {
  qualityControlDispositionEnums,
  qualityControlModuleEnums,
  qualityControlRootCauseCategoryEnums,
  qualityControlResultEnums,
  qualityControlSeverityEnums,
  qualityControlStatusEnums,
} from '../enums';

const qcModuleEnums = qualityControlModuleEnums.map((item) => item.value);
const qcSeverityEnums = qualityControlSeverityEnums.map((item) => item.value);
const qcDispositionEnums = qualityControlDispositionEnums.map(
  (item) => item.value,
);
const qcRootCauseCategoryEnums = qualityControlRootCauseCategoryEnums.map(
  (item) => item.value,
);
const qcStatusEnums = qualityControlStatusEnums.map((item) => item.value);
const qcResultEnums = qualityControlResultEnums.map((item) => item.value);

const preProcessOptionalNumber = z.preprocess((val) => {
  if (val === '' || val === null || val === undefined || Number.isNaN(val))
    return undefined;
  return Number(val);
}, z.number().optional());

const qcBaseBodySchema = z.object({
  // Mandatory
  project: z.coerce.number().min(1, { error: 'Project is required' }),
  release: z.coerce.number().min(1, { error: 'Release is required' }),
  module: z
    .enum(qcModuleEnums)
    .optional()
    .or(z.literal(''))
    .refine((val) => val !== '', {
      message: 'Module is required',
    }),
  severity: z
    .enum(qcSeverityEnums)
    .optional()
    .or(z.literal(''))
    .refine((val) => val !== '', {
      message: 'Severity is required',
    }),
  description: z.string().nonempty({ error: 'Description is required' }),
  qty_affected: z.coerce
    .number()
    .min(1, { error: 'Affected quantity is required' }),
  result: z.enum(qcResultEnums).optional().default('INFO'),

  // Optional
  work_order: preProcessOptionalNumber,
  frame_tag: z.string().optional(),
  part_number: z.string().optional(),
  location: z.string().optional(),
  detected_at: z.string().optional(), // 2026-02-12T16:42:19.904Z
  detected_by: preProcessOptionalNumber,
  spec_ref: z.string().optional(),
  disposition: z.enum(qcDispositionEnums).optional().or(z.literal('')),
  immediate_action: z.string().optional(),
  owner: preProcessOptionalNumber,
  due_date: z.string().optional(), // '2026-02-12'
  root_cause_category: z
    .enum(qcRootCauseCategoryEnums)
    .optional()
    .or(z.literal('')),
  root_cause: z.string().optional(),
  corrective_action: z.string().optional(),
  preventive_action: z.string().optional(),
  purchase_order: preProcessOptionalNumber,
  attachments: z
    .object({
      file_url: z.string().nonempty({ error: 'File url is required' }),
      filename: z.string().nonempty({ error: 'Filename is required' }),
    })
    .array()
    .optional(),
});
const qcBaseResponseSchema = z.object({
  id: z.number(),
  attachments: z
    .object({
      id: z.number(),
      filename: z.string(),
      file: z.string(),
    })
    .array(),
  project_name: z.string(),
  release_number: z.string(),
  created_by_name: z.string(),
  detected_by_name: z.string(),
  closed_by_name: z.string(),
  purchase_order_number: z.string().nullable(),
  purchase_order_reference: z.string().nullable(),
  supplier_name: z.string(),
  work_order_identifier: z.string().nullable(),
  module: z.enum(qcModuleEnums),
  frame_tag: z.string().nullable(),
  part_number: z.string().nullable(),
  location: z.string().nullable(),
  qc_number: z.string(),
  detected_at: z.string().nullable(), // "2026-02-12T16:42:19.905Z"
  severity: z.enum(qcSeverityEnums),
  description: z.string(),
  spec_ref: z.string().nullable(),
  qty_affected: z.number(),
  result: z.enum(qcResultEnums),
  disposition: z.enum(qcDispositionEnums).nullable(),
  immediate_action: z.string().nullable(),
  due_date: z.string().nullable(), // '2026-02-12'
  root_cause_category: z.enum(qcRootCauseCategoryEnums).nullable(),
  root_cause: z.string().nullable(),
  corrective_action: z.string().nullable(),
  preventive_action: z.string().nullable(),
  status: z.enum(qcStatusEnums),
  closed_on: z.date().nullable(), // '2026-02-12'
  closure_notes: z.string().nullable(),
  created_at: z.date(), // '2026-02-12T16:42:19.905Z'
  updated_at: z.date(), // '2026-02-12T16:42:19.905Z'
  project: z.number(),
  release: z.number(),
  work_order: z.number().nullable(),
  detected_by: z.number().nullable(),
  purchase_order: z.number().nullable(),
  supplier: z.number().nullable(),
  owner: z.number().nullable(),
  closed_by: z.number().nullable(),
  created_by: z.number().nullable(),
});

// POST-> /api/quality-control/events/
export const postEvents = {
  body: qcBaseBodySchema,
  response: qcBaseResponseSchema,
  params: z.never(),
  query: z.never(),
};
const postSchema = z.object(postEvents);
export type PostEvents = z.infer<typeof postSchema>;

// GET-> /api/quality-control/events/
const getEventsAll = {
  body: z.never(),
  response: qcBaseResponseSchema
    .pick({
      id: true,
      project: true,
      project_name: true,
      release: true,
      release_number: true,
      qc_number: true,
      description: true,
      module: true,
      severity: true,
      result: true,
      status: true,
      created_at: true,
    })
    .array(),
  params: z.never(),
  query: z
    .object({
      project: z.coerce.number().optional(),
      release: z.coerce.number().optional(),
      status: z.enum(['OPEN', 'CLOSED']).optional(),
    })
    .optional(),
};
const getEventsAllSchema = z.object(getEventsAll);
export type GetEventsAll = z.infer<typeof getEventsAllSchema>;

// GET-> /api/quality-control/events/{id}/
const getEventById = {
  body: z.never(),
  response: qcBaseResponseSchema,
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getEventByIdSchema = z.object(getEventById);
export type GetEventById = z.infer<typeof getEventByIdSchema>;

// PATCH-> /api/quality-control/events/{id}/
export const patchEventById = {
  body: qcBaseBodySchema
    .pick({
      attachments: true,
      module: true,
      frame_tag: true,
      part_number: true,
      location: true,
      detected_at: true,
      severity: true,
      description: true,
      spec_ref: true,
      qty_affected: true,
      disposition: true,
      immediate_action: true,
      due_date: true,
      root_cause_category: true,
      root_cause: true,
      corrective_action: true,
      preventive_action: true,
      status: true,
      closed_on: true,
      closure_notes: true,
      work_order: true,
      detected_by: true,
      purchase_order: true,
      owner: true,
    })
    .partial(),
  response: qcBaseResponseSchema,
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchEventByIdSchema = z.object(patchEventById);
export type PatchEventById = z.infer<typeof patchEventByIdSchema>;

// DELETE-> /api/quality-control/events/{id}/
const deleteEventById = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteEventByIdSchema = z.object(deleteEventById);
export type DeleteEventById = z.infer<typeof deleteEventByIdSchema>;

// GET-> /api/quality-control/events/attachments/{id}/
const getEventAttachmentById = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    filename: z.string(),
    file: z.string(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getEventAttachmentByIdSchema = z.object(getEventAttachmentById);
export type GetEventAttachmentById = z.infer<
  typeof getEventAttachmentByIdSchema
>;

// DELETE-> /api/quality-control/events/attachments/{id}/
const deleteEventAttachmentById = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteEventAttachmentByIdSchema = z.object(deleteEventAttachmentById);
export type DeleteEventAttachmentById = z.infer<
  typeof deleteEventAttachmentByIdSchema
>;

export const closeEventBaseSchema = z.object({
  immediate_action: z
    .string()
    .nonempty({ error: 'Immediate action is required' }),
  frame_tag: z.string().nonempty({ error: 'Frame tag is required' }),
  corrective_action: z
    .string()
    .nonempty({ error: 'Corrective action is required' }),
  preventive_action: z
    .string()
    .nonempty({ error: 'Preventive action is required' }),
  closure_notes: z.string().nonempty({ error: 'Closure notes is required' }),
});
// POST-> /api/quality-control/events/create-close/
export const postCreateCloseEvent = {
  body: qcBaseBodySchema
    .omit({
      immediate_action: true,
      frame_tag: true,
      corrective_action: true,
      preventive_action: true,
      closure_notes: true,
    })
    .extend(closeEventBaseSchema.shape),
  response: qcBaseResponseSchema.pick({
    id: true,
    attachments: true,
    project_name: true,
    release_number: true,
    created_by_name: true,
    detected_by_name: true,
    closed_by_name: true,
    purchase_order_number: true,
    supplier_name: true,
    module: true,
    frame_tag: true,
    part_number: true,
    location: true,
    qc_number: true,
    detected_at: true,
    severity: true,
    description: true,
    spec_ref: true,
    qty_affected: true,
    result: true,
    disposition: true,
    immediate_action: true,
    due_date: true,
    root_cause_category: true,
    root_cause: true,
    corrective_action: true,
    preventive_action: true,
    status: true,
    closed_on: true,
    closure_notes: true,
    created_at: true,
    updated_at: true,
    project: true,
    release: true,
    work_order: true,
    detected_by: true,
    purchase_order: true,
    supplier: true,
    owner: true,
    closed_by: true,
    created_by: true,
  }),
  params: z.never(),
  query: z.never(),
};
const postCreateCloseEventSchema = z.object(postCreateCloseEvent);
export type PostCreateCloseEvent = z.infer<typeof postCreateCloseEventSchema>;

// PATCH-> /api/quality-control/events/{id}/update-close/
export const patchUpdateCloseEvent = {
  body: closeEventBaseSchema,
  response: qcBaseResponseSchema.pick({
    id: true,
    attachments: true,
    project_name: true,
    release_number: true,
    created_by_name: true,
    detected_by_name: true,
    closed_by_name: true,
    purchase_order_number: true,
    supplier_name: true,
    module: true,
    frame_tag: true,
    part_number: true,
    location: true,
    qc_number: true,
    detected_at: true,
    severity: true,
    description: true,
    spec_ref: true,
    qty_affected: true,
    result: true,
    disposition: true,
    immediate_action: true,
    due_date: true,
    root_cause_category: true,
    root_cause: true,
    corrective_action: true,
    preventive_action: true,
    status: true,
    closed_on: true,
    closure_notes: true,
    created_at: true,
    updated_at: true,
    project: true,
    release: true,
    work_order: true,
    detected_by: true,
    purchase_order: true,
    supplier: true,
    owner: true,
    closed_by: true,
    created_by: true,
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchUpdateCloseEventSchema = z.object(patchUpdateCloseEvent);
export type PatchUpdateCloseEvent = z.infer<typeof patchUpdateCloseEventSchema>;
